import { useState, useEffect } from 'react'
import { createCardLink } from '../services/linkService'
import { listenToCardStatus } from '../services/cardStatusService'
import { playNotificationSound, showNotificationPopup } from '../utils/notifications'
import StatusBar from './StatusBar'
import '../styles/components/share-link.css'

function ShareLink({ preferences, booking, deadline, onComplete, onStartOver, responseLink, isResponseLink = false, shortCode: propShortCode = null }) {
  console.log('🟢 ShareLink rendered with isResponseLink =', isResponseLink)
  console.log('responseLink =', responseLink)
  
  const [shareLink, setShareLink] = useState(responseLink || '')
  const [loading, setLoading] = useState(!responseLink && !isResponseLink)
  const [copySuccess, setCopySuccess] = useState(false)
  const [senderHasResponded, setSenderHasResponded] = useState(false)
  const [senderResponses, setSenderResponses] = useState(null)
  const [cardStatus, setCardStatus] = useState('sender_responded')
  const [recipientHasResponded, setRecipientHasResponded] = useState(false)
  const [resultsLink, setResultsLink] = useState(null)
  
  // AUTO-DETECT if this is recipient side by checking URL and responseLink prop
  const actuallyIsResponseLink = isResponseLink || 
    (responseLink && responseLink.includes('?results=')) ||
    (shareLink && shareLink.includes('?results=')) ||
    (window.location.search.includes('?results='))

  useEffect(() => {
    // Check if sender has already responded
    const stored = sessionStorage.getItem('parkort_sender_responses')
    const hasResponded = sessionStorage.getItem('parkort_sender_has_responded') === 'true'
    if (stored) {
      setSenderResponses(JSON.parse(stored))
      setSenderHasResponded(true)
    } else if (hasResponded) {
      setSenderHasResponded(true)
    }
    
    // Check if this is recipient's response link (isResponseLink = true)
    const isRecipientSide = isResponseLink === true
    
    // Check URL for sender_responded param
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.get('sender_responded') === 'true') {
      setSenderHasResponded(true)
    }
    
    // If we have a responseLink, use it directly
    if (responseLink) {
      setShareLink(responseLink)
      setLoading(false)
      return
    }
    
    // Generate link ONLY if this is sender side (not response link)
    if (!isResponseLink && !actuallyIsResponseLink) {
      generateLink()
    } else {
      // For recipient side without responseLink, don't generate - wait for data
      setLoading(false)
    }
  }, [responseLink, isResponseLink])

  // Real-time listener for recipient response
  useEffect(() => {
    if (!shareLink || actuallyIsResponseLink) return
    
    const match = shareLink.match(/\?c=([a-zA-Z0-9]+)/)
    if (!match) return
    
    const shortCode = match[1]
    
    const unsubscribe = listenToCardStatus(shortCode, (result) => {
      if (result.success && result.data) {
        if (result.data.status === 'ready_for_feedback' || result.data.recipientResponses) {
          setRecipientHasResponded(true)
          setCardStatus('ready_for_feedback')
          const resultsUrl = `${window.location.origin}/?r=${shortCode}`
          setResultsLink(resultsUrl)
          
          playNotificationSound()
          showNotificationPopup('Modtageren har svaret!', () => {
            window.location.href = resultsUrl
          })
        }
      }
    })
    
    return () => {
      if (unsubscribe) unsubscribe()
    }
  }, [shareLink, actuallyIsResponseLink])

  const generateLink = async () => {
    try {
      setLoading(true)
      // Combine booking and deadline
      const bookingData = booking ? {
        ...booking,
        deadline: booking.deadline || deadline || null
      } : (deadline ? { deadline } : null)
      
      const link = await createCardLink({
        preferences,
        booking: bookingData
      })
      
      // Ensure we got a valid link
      if (!link) {
        throw new Error('No link generated')
      }
      
      setShareLink(link)
      setLoading(false)
      onComplete()
    } catch (error) {
      console.error('Error generating link:', error)
      // Fallback: Generate link manually
      const bookingDataFallback = booking ? {
        ...booking,
        deadline: booking.deadline || deadline || null
      } : (deadline ? { deadline } : null)
      
      // Determine base path
      const getBasePath = () => {
        const pathname = window.location.pathname
        if (pathname.includes('/dist/') || pathname === '/dist' || pathname.startsWith('/dist')) {
          return '/dist'
        }
        // Check scripts to see if we're in /dist/
        const scripts = document.querySelectorAll('script[src]')
        for (const script of scripts) {
          if (script.src.includes('/dist/')) {
            return '/dist'
          }
        }
        return ''
      }
      
      const basePath = getBasePath()
      const data = encodeURIComponent(JSON.stringify({ preferences, booking: bookingDataFallback }))
      const fallbackLink = `${window.location.origin}${basePath}/?card=${data}`
      setShareLink(fallbackLink)
      setLoading(false)
    } finally {
      // Ensure loading is always set to false
      setLoading(false)
    }
  }

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareLink)
      setCopySuccess(true)
      // Show success message
      const successMsg = document.createElement('div')
      successMsg.style.cssText = `
        position: fixed;
        top: 20px;
        left: 50%;
        transform: translateX(-50%);
        background: rgba(81, 207, 102, 0.95);
        color: white;
        padding: 16px 24px;
        border-radius: 12px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        z-index: 10000;
        animation: slideDown 0.3s ease-out;
      `
      successMsg.textContent = '✅ Link kopieret til udklipsholder!'
      document.body.appendChild(successMsg)
      setTimeout(() => {
        successMsg.style.animation = 'fadeOut 0.3s ease-out'
        setTimeout(() => successMsg.remove(), 300)
      }, 2000)
      setTimeout(() => setCopySuccess(false), 2000)
    } catch (err) {
      // Fallback
      const textArea = document.createElement('textarea')
      textArea.value = shareLink
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      setCopySuccess(true)
      setTimeout(() => setCopySuccess(false), 2000)
    }
  }

  const shareLinkNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: '💌 Du har modtaget et kort',
          text: 'Nogen vil gerne høre din ærlige mening om nogle emner. Vil du svare?',
          url: shareLink
        })
      } catch (err) {
        // User cancelled or error
        // User cancelled share
      }
    } else {
      copyToClipboard()
    }
  }

  const shareToFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareLink)}`
    window.open(url, '_blank', 'width=600,height=400')
  }

  const shareToTwitter = () => {
    const text = encodeURIComponent('Nogen vil gerne høre din ærlige mening om nogle emner. Vil du svare?')
    const url = `https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(shareLink)}`
    window.open(url, '_blank', 'width=600,height=400')
  }

  const shareToWhatsApp = () => {
    const text = encodeURIComponent(`Nogen vil gerne høre din ærlige mening om nogle emner. Vil du svare?\n\n${shareLink}`)
    const url = `https://wa.me/?text=${text}`
    window.open(url, '_blank')
  }

  const shareViaEmail = () => {
    const subject = encodeURIComponent('💌 Du har modtaget et kort')
    const body = encodeURIComponent(`Hej,\n\nNogen vil gerne høre din ærlige mening om nogle emner. Vil du svare?\n\n${shareLink}\n\nMvh`)
    const url = `mailto:?subject=${subject}&body=${body}`
    window.location.href = url
  }

  // Group preferences by topic
  const groupedPreferences = preferences.reduce((acc, pref) => {
    const topicId = pref.topicId || 'other'
    if (!acc[topicId]) {
      acc[topicId] = []
    }
    acc[topicId].push(pref)
    return acc
  }, {})

  const handleSenderRespond = () => {
    // Store that sender needs to respond
    sessionStorage.setItem('parkort_sender_needs_to_respond', 'true')
    sessionStorage.setItem('parkort_sender_preferences', JSON.stringify(preferences))
    sessionStorage.setItem('parkort_sender_booking', JSON.stringify(booking))
    // Navigate to response form
    window.location.href = `/dist/?sender_response=true&card=${encodeURIComponent(JSON.stringify({ preferences, booking }))}`
  }

  const handleViewFeedback = () => {
    // Load card data and show results
    // Extract data from current shareLink if it's a long link
    if (shareLink && shareLink.includes('?card=')) {
      // Already have the data in the link, can navigate to results
      const urlParams = new URLSearchParams(shareLink.split('?')[1])
      const cardData = urlParams.get('card')
      if (cardData) {
        window.location.href = `${window.location.pathname}?results=${cardData}`
      }
    }
  }

  return (
    <div className="share-link step active">
      <h2>Kort lavet og udfyldt af afsender</h2>
      
      {/* Status Bar */}
      {senderHasResponded && !actuallyIsResponseLink && (
        <StatusBar 
          status={cardStatus} 
          showShareMessage={cardStatus !== 'ready_for_feedback'}
        />
      )}
      
      {/* Se feedback knap */}
      {cardStatus === 'ready_for_feedback' && !actuallyIsResponseLink && (
        <div style={{
          marginBottom: '24px',
          textAlign: 'center'
        }}>
          <button
            className="btn"
            onClick={handleViewFeedback}
            style={{
              background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
              color: 'white',
              padding: '16px 32px',
              fontSize: '1.1em',
              fontWeight: '600',
              boxShadow: '0 4px 12px rgba(72, 187, 120, 0.3)'
            }}
          >
            🎉 Se feedback på kortet
          </button>
        </div>
      )}
      
      {/* Sender Response Prompt */}
      {!senderHasResponded && !actuallyIsResponseLink && (
        <div style={{
          marginBottom: '24px',
          padding: '24px',
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)',
          borderRadius: '16px',
          border: '3px solid rgba(102, 126, 234, 0.4)',
          textAlign: 'center',
          boxShadow: '0 8px 32px rgba(102, 126, 234, 0.2)'
        }}>
          <div style={{ fontSize: '3em', marginBottom: '16px' }}>💡</div>
          <h3 style={{ marginBottom: '16px', fontSize: '1.4em', fontWeight: '700', color: 'var(--cyber-blue)' }}>
            ✏️ Svar selv først for bedre match!
          </h3>
          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '20px',
            textAlign: 'left'
          }}>
            <p style={{ marginBottom: '12px', color: 'var(--text-primary)', lineHeight: '1.7', fontSize: '1.05em' }}>
              <strong>Hvorfor skal du svare først?</strong>
            </p>
            <ul style={{ margin: 0, paddingLeft: '24px', lineHeight: '1.8', color: 'var(--text-primary)' }}>
              <li>Systemet sammenligner <strong>jeres svar</strong> og viser hvor I matcher</li>
              <li>Du får <strong>bedre indsigt</strong> i jeres forskelle og ligheder</li>
              <li>Modtageren får <strong>konkret feedback</strong> baseret på jeres sammenligning</li>
              <li>I får begge <strong>match score</strong> og <strong>handlingsforslag</strong></li>
            </ul>
          </div>
          <p style={{ marginBottom: '20px', color: 'var(--text-secondary)', lineHeight: '1.6', fontSize: '0.95em', fontStyle: 'italic' }}>
            💬 <strong>Tip:</strong> Jo ærligere du svarer, jo bedre match score og feedback får I begge.
          </p>
          <button
            className="btn"
            onClick={handleSenderRespond}
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              padding: '14px 28px',
              fontSize: '1em',
              fontWeight: '600'
            }}
          >
            ✏️ Svar på kortet først
          </button>
          <button
            className="btn btn-outline"
            onClick={() => setSenderHasResponded(true)}
            style={{
              marginLeft: '12px',
              padding: '14px 28px',
              fontSize: '1em'
            }}
          >
            Del nu (jeg svarer senere)
          </button>
        </div>
      )}
      
      {/* Sender Has Responded Confirmation */}
      {senderHasResponded && (
        <div style={{
          marginBottom: '24px',
          padding: '20px',
          background: 'linear-gradient(135deg, rgba(81, 207, 102, 0.15) 0%, rgba(102, 126, 234, 0.15) 100%)',
          borderRadius: '12px',
          border: '2px solid rgba(81, 207, 102, 0.3)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2em', marginBottom: '12px' }}>✅</div>
          <h3 style={{ marginBottom: '12px', fontSize: '1.2em', color: '#51cf66' }}>
            Du har svaret på kortet!
          </h3>
          <p style={{ marginBottom: '0', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
            Når modtageren også svarer, sammenligner vi jeres svar og giver jer begge match score og konkret feedback om hvor I er enige og uenige.
          </p>
        </div>
      )}
      
      {/* Value Reminder */}
      <div style={{
        marginBottom: '24px',
        padding: '16px',
        background: 'rgba(81, 207, 102, 0.1)',
        borderRadius: '12px',
        border: '1px solid rgba(81, 207, 102, 0.3)',
        fontSize: '0.95em'
      }}>
        <div style={{ fontWeight: '600', marginBottom: '4px', color: '#51cf66' }}>
          ✅ Kortet er klar til at deles!
        </div>
        <p style={{ margin: 0, color: 'var(--text-primary)' }}>
          {senderHasResponded 
            ? 'Del linket nedenfor med modtageren, så de kan svare og I begge kan se match resultatet.'
            : 'Når modtageren svarer, får I begge en match score og konkret feedback til næste skridt.'}
        </p>
      </div>
      

      {/* Card Preview - What You're Sending */}
      <div className="card-preview-enhanced" style={{
        background: 'var(--glass-surface)',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <div style={{ fontSize: '2em', marginBottom: '8px' }}>💌</div>
          <h3 style={{ margin: 0, fontSize: '1.3em' }}>Dit kort indeholder:</h3>
        </div>
        
        <div className="preferences-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '12px',
          marginBottom: '20px'
        }}>
          {preferences.map((pref, index) => (
            <div 
              key={pref.id || index} 
              className="preference-card"
              style={{
                padding: '12px',
                background: 'rgba(102, 126, 234, 0.1)',
                border: '1px solid rgba(102, 126, 234, 0.2)',
                borderRadius: '8px',
                fontSize: '0.9em',
                lineHeight: '1.4'
              }}
            >
              {pref.text || pref}
            </div>
          ))}
        </div>

        {booking?.date && (
          <div className="card-booking-info" style={{
            padding: '16px',
            background: 'rgba(255, 215, 0, 0.1)',
            border: '1px solid rgba(255, 215, 0, 0.3)',
            borderRadius: '8px',
            marginTop: '16px'
          }}>
            <h4 style={{ margin: '0 0 8px 0', fontSize: '1em' }}>📅 Foreslået tidspunkt:</h4>
            <p style={{ margin: 0, fontSize: '0.95em' }}>
              {new Date(`${booking.date}T${booking.time}`).toLocaleDateString('da-DK', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          </div>
        )}

        {(booking?.deadline || deadline) && (
          <div className="card-deadline-info" style={{
            padding: '12px',
            background: 'rgba(255, 107, 107, 0.1)',
            border: '1px solid rgba(255, 107, 107, 0.3)',
            borderRadius: '8px',
            marginTop: '12px',
            fontSize: '0.9em'
          }}>
            <strong>⏰ Deadline:</strong> {new Date(booking?.deadline || deadline).toLocaleDateString('da-DK', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </div>
        )}
      </div>

      <div className="share-section">
        <div className="share-title" style={{ marginBottom: '16px' }}>
          → Send dette link til den der skal have det
        </div>
        
        {loading ? (
          <div className="loading-state" style={{ 
            textAlign: 'center', 
            padding: '40px 20px' 
          }}>
            <div className="loading-spinner" style={{
              width: '40px',
              height: '40px',
              border: '3px solid rgba(102, 126, 234, 0.2)',
              borderTop: '3px solid var(--cyber-blue)',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px'
            }}></div>
            <p style={{ color: 'var(--text-secondary)' }}>Genererer link...</p>
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        ) : (
          <>
            <div className="link-display" style={{ marginBottom: '16px' }}>
              {shareLink}
            </div>
            
            {actuallyIsResponseLink && (
              <div style={{
                marginBottom: '24px',
                padding: '20px',
                background: 'linear-gradient(135deg, rgba(72, 187, 120, 0.2) 0%, rgba(56, 161, 105, 0.2) 100%)',
                borderRadius: '16px',
                border: '2px solid rgba(72, 187, 120, 0.4)',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '2.5em', marginBottom: '12px' }}>🎉</div>
                <h3 style={{ marginBottom: '12px', fontSize: '1.3em', color: '#48bb78' }}>
                  I har nu begge svaret!
                </h3>
                <p style={{ marginBottom: '0', color: 'var(--text-primary)', lineHeight: '1.6' }}>
                  Klik på "Se jeres feedback" nedenfor for at se jeres match score og konkret feedback om hvor I er enige og uenige.
                </p>
              </div>
            )}
            
            <div className="button-group">
              <button 
                className={`btn ${copySuccess ? 'btn-success btn-large' : ''}`}
                onClick={copyToClipboard} 
                aria-label={actuallyIsResponseLink ? "Kopier results link" : "Kopier link til modtager"}
                style={copySuccess ? {
                  animation: 'pulse 1.5s ease-in-out infinite'
                } : {}}
              >
                {copySuccess ? '✅ Kopieret!' : (actuallyIsResponseLink ? '📋 Kopier results link' : '📋 Kopier link til modtager')}
              </button>
              
              {actuallyIsResponseLink ? (
                <button 
                  className="btn" 
                  onClick={() => {
                    // Navigate to results - SIMPLE direct approach
                    if (responseLink && responseLink.includes('?results=')) {
                      window.location.href = responseLink
                    } else {
                      // Fallback: construct from shareLink
                      const urlParams = new URLSearchParams(shareLink.split('?')[1] || '')
                      const resultsData = urlParams.get('results')
                      if (resultsData) {
                        window.location.href = `${window.location.origin}${window.location.pathname}?results=${resultsData}`
                      }
                    }
                  }}
                  aria-label="Se jeres feedback"
                  style={{
                    background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                    color: 'white',
                    fontWeight: '600'
                  }}
                >
                  🎯 Se jeres feedback
                </button>
              ) : (
                <button 
                  className="btn btn-secondary" 
                  onClick={() => window.open(shareLink, '_blank')}
                  aria-label="Preview"
                >
                  👁️ Preview
                </button>
              )}
            </div>
            
            {/* Social Sharing Buttons */}
            <div className="social-sharing" style={{
              marginTop: '24px',
              paddingTop: '24px',
              borderTop: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <div style={{
                fontSize: '0.9em',
                color: 'var(--text-secondary)',
                marginBottom: '12px',
                textAlign: 'center'
              }}>
                Del på sociale medier:
              </div>
              <div className="social-buttons" style={{
                display: 'flex',
                gap: '8px',
                justifyContent: 'center',
                flexWrap: 'wrap'
              }} role="group" aria-label="Del på sociale medier">
                <button
                  className="btn-social"
                  onClick={shareToFacebook}
                  aria-label="Del på Facebook"
                  style={{
                    padding: '12px 16px',
                    background: '#1877F2',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.9em',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    transition: 'all 0.2s',
                    minHeight: '44px',
                    minWidth: '44px'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                  <span>📘</span> Facebook
                </button>
                <button
                  className="btn-social"
                  onClick={shareToTwitter}
                  aria-label="Del på Twitter"
                  style={{
                    padding: '12px 16px',
                    background: '#1DA1F2',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.9em',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    transition: 'all 0.2s',
                    minHeight: '44px',
                    minWidth: '44px'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                  <span>🐦</span> Twitter
                </button>
                <button
                  className="btn-social"
                  onClick={shareToWhatsApp}
                  aria-label="Del på WhatsApp"
                  style={{
                    padding: '12px 16px',
                    background: '#25D366',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.9em',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    transition: 'all 0.2s',
                    minHeight: '44px',
                    minWidth: '44px'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                  <span>💬</span> WhatsApp
                </button>
                <button
                  className="btn-social"
                  onClick={shareViaEmail}
                  aria-label="Del via email"
                  style={{
                    padding: '12px 16px',
                    background: 'var(--cyber-blue)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.9em',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    transition: 'all 0.2s',
                    minHeight: '44px',
                    minWidth: '44px'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                  <span>✉️</span> Email
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      
      {!actuallyIsResponseLink && (
        <div className="button-group">
          <button className="btn btn-secondary" onClick={onStartOver}>
            ← Lav nyt kort
          </button>
        </div>
      )}
    </div>
  )
}

export default ShareLink
